
import { defineComponent } from "vue";

import ConfirmDialog from "@/ChildrenComponents/ConfirmDialog.vue";
import LoadingSpinner from "@/ChildrenComponents/LoadingSpinner.vue";
import ValidationAlert from "@/ChildrenComponents/ValidationAlert.vue";
import PageHeader from "@/ChildrenComponents/Participant/Common/PageHeader.vue";
import ParticipantHeader from "@/ChildrenComponents/Participant/Common/ParticipantHeader.vue";
import SingleErrorMessage from "@/ChildrenComponents/Participant/Common/SingleErrorMessage.vue";
import ReportExporter from '@/ChildrenComponents/ReportExporter.vue';
import AddMedicationDialog from '@/ChildrenComponents/Participant/AddMedicationDialog.vue';

import HeaderData from "@/Models/HeaderData";
import { ComponentType } from "@/Enums/ComponentType";
import { IInternalUser } from "@/Models/IInternalUser";
import { IDataTableHeader } from "@/Models/IDataTableHeader";
import { IMedication, MedicationForEdit } from "@/Models/IMedication";
import { ICase } from "@/Models/ICase";

import { MedicationService } from "@/Services/medication-service";
import { DateHelper } from "@/Services/Helper/date-helper";
import { loadParticipantDataRequireCase } from "@/Services/Helper/loader-helper";

import { doesUserRoleHaveComponentEditPermissions } from "@/Services/Helper/component-permissions-helper";
import { useUserStore } from "@/Services/Store/user-store";

export default defineComponent({
	name: "medication-dashboard",
	components: {
		LoadingSpinner,
		ParticipantHeader,
		ConfirmDialog,
		ValidationAlert,
		PageHeader,
		SingleErrorMessage,
		ReportExporter,
		AddMedicationDialog,
	},
	setup(): { currentUser: IInternalUser | null } {
		const userStoreInstance = useUserStore();
		return { currentUser: userStoreInstance.$state.cstInternalUser };
	},
	created() {
		this.setBlankItem();
		loadParticipantDataRequireCase(this.pageType, this.id).then(data => {
			this.caseId = data.case?.caseId ?? null;
			this.caseData = data.case;
			this.headerData = new HeaderData(data.participant, data.case?.program ?? null);
		}).then(() => {
			if (this.caseId != null) {
				MedicationService.getByCaseId(this.caseId).then(result => this.items = result).finally(() => this.isLoading = false);
			}
		}).catch((error) => (this.errorMessage = error));
	},
	data() {
		return {
			isLoading: true,
			errorMessage: "",
			validationMessages: [] as string[],
			pageType: this.$route.params.type?.toString() ?? "",
			headerData: null as HeaderData | null,
			id: this.$route.params.id == undefined ? null : parseInt(this.$route.params.id.toString()),
			caseId: null as number | null,

			items: [] as IMedication[],
			selectedItem: new MedicationForEdit({} as IMedication),
			caseData: {} as ICase | null,
			headers: [
				{ title: "Medication", key: "medication", sortable: true, align: "start" },
				{ title: "Provider", key: "provider", sortable: true, align: "start" },
				{ title: "Dosage", key: "dosage", sortable: true, align: "start" },
				{ title: "Refills", key: "refills", sortable: true, align: "start" },
				{ title: "Start Date", key: "startDate", sortable: true, align: "start" },
				{ title: "End Date", key: "endDate", sortable: true, align: "start" },
				{ title: "Edit", key: "actions", sortable: false },
				{ title: "Case", key: "case", sortable: false }
			] as IDataTableHeader[],
			reportHeaders: [
				{ title: "Medication", key: "medication", sortable: true, align: "start" },
				{ title: "Provider", key: "provider", sortable: true, align: "start" },
				{ title: "Dosage", key: "dosage", sortable: true, align: "start" },
				{ title: "Refills", key: "refills", sortable: true, align: "start" },
				{ title: "Start Date", key: "startDateFormatted", sortable: true, align: "start" },
				{ title: "End Date", key: "endDateFormatted", sortable: true, align: "start" },
			] as IDataTableHeader[],

			showDeleteWarning: false,
			deleteWarningText: "Are you sure you want to delete this medication? This cannot be undone!",
			idToDelete: null as number | null,
			search: "",
			showDialog: false,
		};
	},
	methods: {
		formatDate(date: Date | null): string {
			return DateHelper.formatDateUtc(date, "MM/dd/yyyy");
		},
		setBlankItem() {
			const blankItem = { medicationId: 0, caseId: this.caseId } as IMedication;
			this.selectedItem = new MedicationForEdit(blankItem);
		},
		newItem() {
			this.setBlankItem();
			this.showDialog = true;
		},
		editItem(item: IMedication) {
			this.selectedItem = new MedicationForEdit(item);
			this.showDialog = true;
		},
		cancelEdit() {
			this.setBlankItem();
			this.showDialog = false;
		},
		itemSaved(newItem: IMedication) {
			const index = this.items.findIndex(x => x.medicationId == newItem.medicationId);
			if (index > -1) {
				this.items[index] = newItem;
			}
			else {
				this.items.push(newItem);
			}
			this.items.sort((x, y) => DateHelper.standardizeDate(x.startDate)!.getTime() - DateHelper.standardizeDate(y.startDate)!.getTime());

			this.setBlankItem();
			this.showDialog = false;
		},
		navigateToCase(caseId: number) {
			this.$router.push(`/case/${caseId}`);
		},
		deleteItem(id: number) {
			this.idToDelete = id;
			this.showDeleteWarning = true;
		},
		cancelDelete() {
			this.showDeleteWarning = false;
			this.idToDelete = null;
		},
		confirmDelete() {
			this.showDeleteWarning = false;
			if (this.idToDelete != null) {
				MedicationService.delete(this.idToDelete)
					.then(() => this.items = this.items.filter(x => x.medicationId != this.idToDelete));
			}
		},
	},
	computed: {
		canEditRolePermission(): boolean {
			return doesUserRoleHaveComponentEditPermissions(this.currentUser?.attachedRoles ?? [], ComponentType.MedicationDashboard);
		},
        reportData(): any[] {
            return this.items.map((x: IMedication) => {
                return {
                    ...x,
                    startDateFormatted: this.formatDate(x.startDate),
                    endDateFormatted: this.formatDate(x.endDate),
                };
            })
        },
	},
});
